/**
 * Top-level chrome composer — ported from ThemeMaker's
 * {@code lms-portal/src/components/shell/SiteShellPresenter.tsx}.
 *
 * <p>Wraps {@code <main>} with TopBar → Header → main → Footer (or
 * sidebar layout). The structural skeleton is fixed; the components
 * rendering each region come from {@link HeaderRenderer} +
 * {@link FooterRenderer} which dispatch on {@code shell.{header,footer}.style}.
 *
 * <p>Cross-cutting concerns the presenter wires:
 * <ul>
 *   <li><b>Auth state</b> — {@code currentUser} flows down so headers
 *       / footers can gate items by {@code requireAuth} /
 *       {@code requirePerms}. The same pair on
 *       {@code header} / {@code footer} themselves ("Visible to" in
 *       the admin Shell editor's Advanced group) gates the WHOLE
 *       region: a gated-out header renders headerless and drops the
 *       sidebar layout, a gated-out footer renders nothing.</li>
 *   <li><b>Locale</b> — passed to {@link HeaderRenderer} /
 *       {@link FooterRenderer} which use {@code t()} to resolve any
 *       {@code LocalizedString} in the shell config.</li>
 *   <li><b>Sidebar layout</b> — when {@code header.style} is
 *       {@code 'sidebar'} (or {@code 'sidebar-collapsible'}) the whole
 *       shell flips to a horizontal flex row with the rail on the left
 *       and TopBar / main / Footer stacked in a column to its right.</li>
 *   <li><b>Overlay mode</b> — when {@code header.overlay === true}
 *       and the style supports it (cinematic-immersive), the header
 *       becomes a floating element layered over main content. A
 *       {@code <style>} block injects the {@code .lms-main--overlay}
 *       top-padding rule + the {@code [data-bleeds-under-header="true"]}
 *       opt-out for hero blocks that should bleed full-viewport.</li>
 *   <li><b>Rounded frame</b> — when {@code shell.frame === 'rounded'}
 *       the whole shell is wrapped in a soft card with a rounded
 *       border (Studio-light "app-in-a-card" aesthetic).</li>
 * </ul>
 *
 * <h3>Pure presentation</h3>
 * No {@code next/headers} / {@code next/cache} / {@code 'use server'}
 * imports — those belong in the host's mount layer (which reads
 * accountConfig, computes tenantHref, defines {@code setLocaleAction}
 * as a server action, then mounts {@code SiteShellPresenter} as
 * children-receiver).
 */
import type { CSSProperties, ReactNode } from 'react';
import type { CurrentUser, ShellConfig } from '../portal';
import { BrandContext, DEFAULT_I18N, isVisible, t } from '../portal';
import {
  HeaderRenderer,
  TopBar,
  SidebarTopBar,
  SidebarMobileBar,
  isHeaderVisible,
  type HeaderProps,
} from './headers';
import { FooterRenderer, type FooterProps } from './footers';
import { shellHref } from './shellHref';
import { ScrollToTopButton } from './widgets/ScrollToTopButton';
import { HelpDeskWidget } from './widgets/HelpDeskWidget';

/** Default shell config used when a tenant hasn't published one yet. */
export const DEFAULT_SHELL: ShellConfig = {
  brand: { name: 'Academy', logoHref: '/' },
  header: {
    style: 'classic',
    // LocalizedString, not a bare literal: shell menu labels are resolved
    // by the platform `t(value, locale)` below, so the placeholder menu
    // follows the request locale like an authored one does.
    menu: [{ kind: 'link', label: { tr: 'Ana Sayfa', en: 'Home' }, href: '/' }],
    showSearch: false,
    showProfile: true,
  },
  footer: {
    style: 'classic',
    copyright: '© Academy',
  },
};

export interface SiteShellPresenterProps {
  config: ShellConfig | null | undefined;
  tenantSlug: string;
  currentUser: CurrentUser | null;
  locale: string;
  setLocaleAction: (form: FormData) => void | Promise<void>;
  children: ReactNode;
  /** Optional override for the header search affordance. */
  headerSearchSlot?: ReactNode;
  /** Optional category strip rendered between header and main — used
   *  by content-module surfaces (blog/video/micro) that want
   *  shell-adjacent chrome. */
  headerCategorySlot?: ReactNode;
  /** Per-render override for the shell's {@code categoriesMenu}
   *  component (if present in the active config). */
  categoriesMenuOverride?: { feedSource: 'courses' | 'blog' | 'video' | 'micro' };
  /** Tenant-private header resolver — forwarded to
   *  {@link HeaderRenderer}. Hosts that ship {@code @akademi/theme-btk}
   *  pass {@code resolveCustomHeader} here so the {@code btk} /
   *  {@code sidebar-collapsible} styles render. */
  resolveCustomHeader?: HeaderProps['resolveCustomHeader'];
  /** Tenant-private footer resolver — mirror of {@link resolveCustomHeader}.
   *  Forwarded to {@link FooterRenderer} so themes can ship a custom
   *  footer (none today, but the seam exists). */
  resolveCustomFooter?: FooterProps['resolveCustomFooter'];
  /** Numeric tenant ID — forwarded to catalog search widgets.
   *  Host reads it from {@code client.getTenantId()}. */
  tenantId?: string | number;
  /** Full path to the catalog search proxy route (includes app base path).
   *  Pass BASEPATH + '/api/catalog/search'. */
  catalogApiPath?: string;
}

export function SiteShellPresenter({
  config,
  tenantSlug,
  currentUser,
  locale,
  setLocaleAction,
  children,
  headerSearchSlot,
  headerCategorySlot,
  categoriesMenuOverride,
  resolveCustomHeader,
  resolveCustomFooter,
  tenantId,
  catalogApiPath,
}: SiteShellPresenterProps) {
  const cfg = config ?? DEFAULT_SHELL;

  // Resolved once here and threaded via BrandContext so any block
  // rendered inside `children` (e.g. a course card's no-image fallback)
  // can show the tenant's own mark instead of a generic placeholder,
  // without the shell config being drilled through every block's props.
  const brandFallback = cfg.i18n?.default ?? DEFAULT_I18N.default;
  const brand = {
    name: t(cfg.brand?.name, locale, brandFallback) || undefined,
    logo: cfg.brand?.logo,
    logoDark: cfg.brand?.logoDark,
  };
  const brandedChildren = <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;

  // Overlay-mode top-padding CSS, injected once. In overlay mode
  // (`header.overlay === true`, e.g. cinematic-immersive) <main> needs
  // to reserve header height as top padding so regular content starts
  // BELOW the floating header. Pages whose first block is bleed-capable
  // opt out via a `data-bleeds-under-header="true"` attribute that the
  // BlockRenderer sets — the `:has()` selectors here detect it and
  // zero the padding so the hero bleeds up under the header.
  const overlayCss = `
.lms-main--overlay {
  padding-top: var(--cinematic-header-height, 88px);
}
.lms-main--overlay:has(> [data-slot] [data-bleeds-under-header="true"]:first-child),
.lms-main--overlay:has(> [data-slot]:first-child > [data-bleeds-under-header="true"]) {
  padding-top: 0;
}
`.trim();

  // Normalises bare hrefs ("blog" → "/blog") so chrome links stay
  // site-root paths, then applies the tenant segment. See shellHref.
  const tenantHref = (href: string | undefined) => shellHref(href, tenantSlug);

  // Header-level "Visible to" gate (header.requireAuth /
  // header.requirePerms, authored under Header → Advanced in the admin
  // Shell editor). HeaderRenderer enforces it on its own too, but the
  // layout has to know: a gated-out sidebar header would otherwise
  // leave an empty rail column plus its SidebarTopBar / mobile-bar
  // companions behind. Hiding it collapses the shell to the plain
  // stacked layout — the same shape `style: 'none'` produces.
  const headerVisible = isHeaderVisible(cfg, currentUser);

  // Sidebar styles flip the shell to a horizontal layout: a fixed
  // vertical nav rail on the left, TopBar / Footer stacked into a
  // column to its right. Both 'sidebar' (server-rendered) and
  // 'sidebar-collapsible' (client, with collapse toggle) trigger
  // the horizontal layout.
  const headerStyle = cfg.header?.style ?? 'classic';
  const isSidebar =
    headerVisible && (headerStyle === 'sidebar' || headerStyle === 'sidebar-collapsible');

  // 'rounded' wraps the entire shell in a card with margin, large
  // corner radius, and a soft shadow — the "app-in-a-card" aesthetic.
  // 'flush' (default) keeps the shell edge-to-edge.
  const frame = cfg.frame ?? 'flush';
  const isRounded = frame === 'rounded';

  // Floating "back to top" button — opt-in per tenant from the admin
  // Shell editor's Theme defaults tab. Rendered once at the shell root
  // (it's position:fixed to the viewport, so DOM placement is only about
  // being present on every page). Off unless the operator enables it.
  const scrollToTop = cfg.theme?.scrollToTop;
  const scrollToTopNode =
    scrollToTop?.show === true ? (
      <ScrollToTopButton position={scrollToTop.position ?? 'bottom-right'} />
    ) : null;

  // Floating help-desk support widget — same "render once at the shell
  // root" reasoning as the back-to-top button above. Gated on `show`
  // plus the usual `requireAuth` visibility check other sections use.
  const helpDesk = cfg.helpDesk;
  const helpDeskNode =
    helpDesk?.show === true && isVisible(helpDesk, currentUser) ? (
      <HelpDeskWidget config={helpDesk} />
    ) : null;

  let inner: ReactNode;

  if (isSidebar) {
    inner = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: isRounded ? '100%' : '100vh',
          height: isRounded ? '100%' : undefined,
        }}
      >
        <TopBar config={cfg} tenantHref={tenantHref} currentUser={currentUser} locale={locale} />
        {/* Mobile-only top strip for sidebar layouts. On <768px the
         *  desktop sidebar is hidden; this gives the user a brand +
         *  hamburger to reach navigation. Lives in the presenter (not
         *  inside SidebarTopBar) so it isn't suppressed by
         *  header.compact. */}
        <SidebarMobileBar
          config={cfg}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          setLocaleAction={setLocaleAction}
          tenantSlug={tenantSlug}
          tenantId={tenantId}
          catalogApiPath={catalogApiPath}
        />
        <div
          className="lms-sidebar-shell"
          style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0 }}
        >
          <HeaderRenderer
            searchOverride={headerSearchSlot}
            categoriesMenuOverride={categoriesMenuOverride}
            resolveCustomHeader={resolveCustomHeader}
            config={cfg}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            setLocaleAction={setLocaleAction}
            tenantSlug={tenantSlug}
            tenantId={tenantId}
            catalogApiPath={catalogApiPath}
          />
          {/* Right column. CSS vars below tell full-bleed blocks that
           *  "100vw of the viewport" is NOT their layout context —
           *  their container is this column, which is
           *  `viewport - sidebar_width`. Without these vars the blocks
           *  would 100vw past the column edges and push the sidebar
           *  off-screen or create horizontal scroll. */}
          <div
            style={
              {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                // `clip` trims full-bleed 100vw blocks without creating a
                // scroll container — unlike `hidden` which makes overflow-y
                // implicitly `auto`, which breaks position:sticky on
                // SidebarTopBar by making THIS div the sticky's container
                // instead of the document scroll root.
                overflowX: 'clip',
                ['--page-bleed-width' as string]: '100%',
                ['--page-bleed-margin' as string]: '0',
              } as CSSProperties
            }
          >
            {!cfg.header?.compact && (
              <SidebarTopBar
                config={cfg}
                tenantHref={tenantHref}
                currentUser={currentUser}
                locale={locale}
                setLocaleAction={setLocaleAction}
                tenantSlug={tenantSlug}
                searchOverride={headerSearchSlot}
                categoriesMenuOverride={categoriesMenuOverride}
                resolveCustomHeader={resolveCustomHeader}
                tenantId={tenantId}
                catalogApiPath={catalogApiPath}
              />
            )}
            {headerCategorySlot && <CategoryStrip>{headerCategorySlot}</CategoryStrip>}
            <main
              id="lms-main"
              tabIndex={-1}
              // flex column so the page content area (#main-content, flex-1)
              // fills main's height — lets pages center content vertically
              // (e.g. the auth card) and keeps the footer at the bottom.
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                // Own stacking + positioning context so a BackgroundGlow
                // block dropped at page root (z-index:-1) paints above
                // this surface yet behind the page content.
                position: 'relative',
                isolation: 'isolate',
                // Solid surface + optional top→bottom degrade. The
                // `color.surface.pageGradient` token (admin, light + dark)
                // tints the top and fades to transparent; unset → solid.
                backgroundColor: 'var(--color-surface-page, #f7f8fa)',
                backgroundImage:
                  'linear-gradient(to bottom, var(--color-surface-pageGradient, transparent), transparent)',
              }}
            >
              {brandedChildren}
            </main>
            <FooterRenderer
              config={cfg}
              tenantHref={tenantHref}
              currentUser={currentUser}
              locale={locale}
              resolveCustomFooter={resolveCustomFooter}
            />
          </div>
        </div>
      </div>
    );
  } else {
    // Overlay mode: header becomes a floating element layered over
    // main content rather than taking its own row. Used by streaming-
    // grade landing pages where the hero should fill the viewport
    // behind the chrome. Only honoured for styles that handle their
    // own visual surface (cinematic-immersive primarily). Headerless
    // ('none') forces overlay off — no header to overlay. Same for a
    // header hidden by its "Visible to" gate: `main` must not reserve
    // header height for chrome that isn't rendering.
    const overlay = cfg.header?.overlay === true && headerStyle !== 'none' && headerVisible;

    inner = (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: isRounded ? '100%' : '100vh',
          height: isRounded ? '100%' : undefined,
          // When overlay is on, the wrapper becomes the positioning
          // context for the absolute-positioned header. Without
          // position:relative the header would anchor to the body,
          // breaking the rounded-frame case.
          position: overlay ? 'relative' : undefined,
        }}
      >
        <TopBar config={cfg} tenantHref={tenantHref} currentUser={currentUser} locale={locale} />
        {overlay ? (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 30,
              // Keep pointer events on the header itself; main content
              // still scrolls normally underneath.
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto' }}>
              <HeaderRenderer
                searchOverride={headerSearchSlot}
                categoriesMenuOverride={categoriesMenuOverride}
                resolveCustomHeader={resolveCustomHeader}
                config={cfg}
                tenantHref={tenantHref}
                currentUser={currentUser}
                locale={locale}
                setLocaleAction={setLocaleAction}
                tenantSlug={tenantSlug}
                tenantId={tenantId}
                catalogApiPath={catalogApiPath}
              />
            </div>
          </div>
        ) : (
          <HeaderRenderer
            searchOverride={headerSearchSlot}
            categoriesMenuOverride={categoriesMenuOverride}
            resolveCustomHeader={resolveCustomHeader}
            config={cfg}
            tenantHref={tenantHref}
            currentUser={currentUser}
            locale={locale}
            setLocaleAction={setLocaleAction}
            tenantSlug={tenantSlug}
            tenantId={tenantId}
            catalogApiPath={catalogApiPath}
          />
        )}
        {/* Category strip lives between header and main. Skipped in
         *  overlay mode — the hero claims the full viewport and a
         *  strip would visually fight with the floating header. */}
        {!overlay && headerCategorySlot && <CategoryStrip>{headerCategorySlot}</CategoryStrip>}
        <main
          id="lms-main"
          tabIndex={-1}
          className={overlay ? 'lms-main lms-main--overlay' : 'lms-main'}
          style={{
            flex: 1,
            minWidth: 0,
            // flex column so #main-content (flex-1) fills main's height —
            // enables vertical centering of page content (e.g. the auth card)
            // while keeping content top-aligned for normal pages.
            display: 'flex',
            flexDirection: 'column',
            // Own stacking + positioning context so a BackgroundGlow
            // block dropped at page root (z-index:-1) paints above this
            // surface yet behind the page content.
            position: 'relative',
            isolation: 'isolate',
            // Overlay mode: main starts at viewport top (no header
            // above it). Page bg switches to dark by default because
            // the only style that asks for overlay is cinematic-
            // immersive which expects dark surfaces.
            backgroundColor: overlay
              ? 'var(--color-surface-page, #07080d)'
              : 'var(--color-surface-page, #f7f8fa)',
            // Optional top→bottom degrade over the solid surface, driven by
            // the `color.surface.pageGradient` token (admin, light + dark);
            // unset → solid page.
            backgroundImage:
              'linear-gradient(to bottom, var(--color-surface-pageGradient, transparent), transparent)',
          }}
        >
          {brandedChildren}
        </main>
        <FooterRenderer
          config={cfg}
          tenantHref={tenantHref}
          currentUser={currentUser}
          locale={locale}
          resolveCustomFooter={resolveCustomFooter}
        />
      </div>
    );
  }

  if (!isRounded) {
    return (
      <>
        <style dangerouslySetInnerHTML={{ __html: overlayCss }} />
        {inner}
        {scrollToTopNode}
        {helpDeskNode}
      </>
    );
  }

  // Rounded frame: wrap the inner shell in a card on a softer page
  // background. Outer container takes 100vh so the shell still fills
  // the viewport on long pages; min-height allows the page to grow
  // when content exceeds 100vh.
  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: overlayCss }} />
      <div
        style={{
          background: 'var(--color-shell-frame-bg, var(--color-neutral-100, #ececef))',
          padding: 'clamp(8px, 1.2vw, 20px)',
          minHeight: '100vh',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            background: 'var(--color-surface-card, #fff)',
            borderRadius: 'clamp(14px, 1.6vw, 24px)',
            // `clip` preserves the rounded-corner visual trim without
            // creating a scroll container — `hidden` would make sticky
            // children anchor to this div instead of the viewport.
            overflow: 'clip',
            boxShadow: 'var(--shadow-lg, 0 12px 32px rgba(0,0,0,0.14))',
            minHeight: 'calc(100vh - 2 * clamp(8px, 1.2vw, 20px))',
            // Anchor for absolutely-positioned descendants (the
            // collapsible sidebar's mobile drawer etc.) so they stay
            // inside the rounded card rather than escaping to the
            // viewport.
            position: 'relative',
          }}
        >
          {inner}
        </div>
      </div>
      {scrollToTopNode}
      {helpDeskNode}
    </>
  );
}

// =============================================================================
// CategoryStrip — thin horizontal bar between header and main
// =============================================================================
//
// Renders the slot content inside a strip with a bottom border,
// matching the visual cadence of YouTube's category strip directly
// under the masthead. Slot content owns its own internal layout
// (chip row, vertical list, etc.); the wrapper only provides chrome.

function CategoryStrip({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        background: 'var(--color-surface-card, #fff)',
        borderBottom: '1px solid var(--color-border-subtle, var(--color-neutral-200, #e5e7eb))',
        padding: 'var(--space-2-5, 10px) clamp(var(--space-4, 16px), 3vw, var(--space-8, 32px))',
      }}
    >
      {children}
    </div>
  );
}
